import React from "react";
import { CartItem, LicenseType } from "../../types/store";
import { useCart } from "../../contexts/CartContext";

interface CartItemRowProps {
    item: CartItem;
}

const typeCopy: Record<CartItem["productType"], string> = {
    TEXTURE: "Texture Pack",
    STL: "STL Collection",
    PHYSICAL: "Physical Edition",
    FIGUR_LAB: "Figur Lab"
};

const CartItemRow: React.FC<CartItemRowProps> = ({ item }) => {
    const { updateQuantity, updateLicense, removeItem } = useCart();
    const allowQuantity = !item.isDigital;
    const showLicense = item.productType === "TEXTURE" || item.productType === "STL";

    const handleQtyChange = (delta: number) => {
        updateQuantity(item.id, item.quantity + delta);
    };

    const handleLicenseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        updateLicense(item.id, event.target.value as LicenseType);
    };

    return (
        <div className="flex gap-4 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div
                className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-xl bg-black/30"
                style={{ backgroundImage: `url(${item.thumbnailUrl})`, backgroundSize: "cover" }}
            />

            <div className="flex-1 space-y-2">
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-xs uppercase tracking-[0.25em] text-white/60">{typeCopy[item.productType]}</p>
                        <p className="text-lg font-semibold">{item.name}</p>
                        {item.shortInfo && <p className="text-sm text-white/60">{item.shortInfo}</p>}
                        <p className="text-xs text-white/50">
                            {item.isDigital ? "Instant download after purchase" : "Ships in 3–5 business days"}
                        </p>
                    </div>
                    <button
                        className="text-white/50 hover:text-white"
                        aria-label="Remove item"
                        onClick={() => removeItem(item.id)}
                    >
                        ×
                    </button>
                </div>

                {showLicense && (
                    <label className="block text-xs text-white/70">
                        License type
                        <select
                            value={item.license ?? "PERSONAL"}
                            onChange={handleLicenseChange}
                            className="mt-1 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm"
                        >
                            <option value="PERSONAL">Personal use</option>
                            <option value="COMMERCIAL">Commercial use</option>
                        </select>
                    </label>
                )}

                <div className="flex items-center justify-between">
                    {allowQuantity ? (
                        <div className="flex items-center gap-3 text-sm">
                            <button
                                onClick={() => handleQtyChange(-1)}
                                className="h-8 w-8 rounded-full border border-white/10"
                                disabled={item.quantity <= 1}
                            >
                                −
                            </button>
                            <span className="w-6 text-center font-semibold">{item.quantity}</span>
                            <button onClick={() => handleQtyChange(1)} className="h-8 w-8 rounded-full border border-white/10">
                                +
                            </button>
                        </div>
                    ) : (
                        <p className="text-sm text-white/70">Qty: 1 (fixed)</p>
                    )}

                    <div className="text-right">
                        <p className="text-sm text-white/60">Unit</p>
                        <p className="text-lg font-semibold">${item.unitPrice.toFixed(2)}</p>
                        <p className="text-xs text-white/50">Line: ${(item.unitPrice * item.quantity).toFixed(2)}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CartItemRow;
